import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import {SaveOutlined} from '@material-ui/icons';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import CharacterCounter from '../../../shared/charactercounter/CharacterCounter.jsx';
import EditorTexto from '../../components/EditorTexto.jsx';
import {
    htmlVazio,
    montarPayloadDocumento,
    ORDEM_MAXIMA,
    TAMANHO_MAXIMO_HTML,
    TAMANHO_MAXIMO_NOME
} from '../../components/cadastro/cadastroBasico.js';

const FormSchema = Yup.object().shape({
    ordem: Yup.number()
        .typeError('É obrigatória')
        .required('É obrigatória')
        .integer('Informe um número inteiro')
        .min(0, `Informe um valor entre 0 e ${ORDEM_MAXIMA}`)
        .max(ORDEM_MAXIMA, `Informe um valor entre 0 e ${ORDEM_MAXIMA}`),
    nome: Yup.string().trim()
        .required('É obrigatório')
        .max(TAMANHO_MAXIMO_NOME, `Máximo de ${TAMANHO_MAXIMO_NOME} caracteres`),
    descricao: Yup.string()
        .test('descricao-obrigatoria', 'É obrigatória', valor => !htmlVazio(valor))
        .test('descricao-tamanho', `Máximo de ${TAMANHO_MAXIMO_HTML} caracteres`, valor => (valor || '').length <= TAMANHO_MAXIMO_HTML)
});

const VALORES_VAZIOS = {ordem: '', nome: '', descricao: ''};

const useStyles = makeStyles(() => ({
    paper: {
        width: 800,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    }
}));

/*
 * Cria ou edita um Documento (ordem, nome e descrição em HTML). `item` vem
 * preenchido na edição e nulo na criação. Ao salvar devolve o corpo
 * {ordem, nome, descricao}; quem grava (e fecha o diálogo) é a tela.
 */
export default function DialogDocumento({open, onClose, item, salvando, onSalvar}) {
    const classes = useStyles();
    const {register, control, handleSubmit, reset, watch, formState: {errors}} = useForm({
        resolver: yupResolver(FormSchema),
        defaultValues: VALORES_VAZIOS
    });

    const preencher = () => reset(item
        ? {
            ordem: item.ordem === null || item.ordem === undefined ? '' : item.ordem,
            nome: item.nome || '',
            descricao: item.descricao || ''
        }
        : VALORES_VAZIOS);

    useEffect(() => {
        if (open) {
            preencher();
        }
    }, [open, item]);

    const submit = values => onSalvar(montarPayloadDocumento(values));

    const tamanhoHtml = (watch('descricao') || '').length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionProps={{onEntering: preencher}}
            scroll='body'
            aria-labelledby='dialog-documento-titulo'
            classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-documento-titulo'>
                {item ? 'Editar Documento' : 'Adicionar Documento'}
            </DialogTitle>
            <form noValidate autoComplete='off' onSubmit={handleSubmit(submit)}>
                <DialogContent classes={{root: classes.contentRoot}}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={3}>
                            <LabelValue label='Ordem *'>
                                <FormControl variant='outlined' fullWidth>
                                    <OutlinedInput
                                        {...register('ordem')}
                                        type='number'
                                        autoFocus
                                        error={!!errors.ordem}
                                        inputProps={{min: 0, max: ORDEM_MAXIMA, step: 1}}/>
                                    {!!errors.ordem && <FormHelperText error>{errors.ordem.message}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <LabelValue label='Nome *'>
                                <FormControl variant='outlined' fullWidth>
                                    <OutlinedInput
                                        {...register('nome')}
                                        error={!!errors.nome}
                                        inputProps={{maxLength: TAMANHO_MAXIMO_NOME}}/>
                                    {!!errors.nome && <FormHelperText error>{errors.nome.message}</FormHelperText>}
                                </FormControl>
                                <CharacterCounter characterQuantityMax={TAMANHO_MAXIMO_NOME} value={watch('nome')}/>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12}>
                            <LabelValue label='Descrição *'>
                                <FormControl variant='outlined' fullWidth error={!!errors.descricao}>
                                    <Controller
                                        name='descricao'
                                        control={control}
                                        render={({field}) => (
                                            <EditorTexto
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                error={!!errors.descricao}/>
                                        )}/>
                                    {!!errors.descricao && <FormHelperText error>{errors.descricao.message}</FormHelperText>}
                                    <FormHelperText error={tamanhoHtml > TAMANHO_MAXIMO_HTML}>
                                        {tamanhoHtml}/{TAMANHO_MAXIMO_HTML}
                                    </FormHelperText>
                                </FormControl>
                            </LabelValue>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <TextButton onClick={onClose}>Fechar</TextButton>
                    <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={salvando}>
                        Salvar
                    </ContainedButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}

DialogDocumento.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    item: PropTypes.object,
    salvando: PropTypes.bool,
    onSalvar: PropTypes.func.isRequired
};
